import dayjs, { Dayjs } from 'dayjs';
import { Connection } from 'mysql2/promise';
import { v4 } from 'uuid';
import { ACTIVITY_CHANGE_TYPE, ChiroUpDayJsCommon } from '../constants';
import { ChiroUpJSON, createUTCDayjs } from '../functions';
import { AppointmentClass } from './Appointment.class';
import {
  AppointmentInsuranceType,
  AppointmentStatuses,
} from './Appointment.type';
import { CodeSets } from './BillingCode.type';
import { Invoice } from './Invoice.type';
import { Patient } from './Patient.type';
import {
  PatientPayment,
  PaymentType,
  TransactionPayment,
} from './PatientPayment.type';
import { Payor } from './Payor.type';

export type CreditTransactionsNeedUpdatedType = {
  billingKey: string;
  item: PatientTransactionItemType;
};

export type TransactionPatientType = {
  id: string;
  displayName: string;
};

/**
 * This is the minimal information to build a transaction.
 */
export type TransactionPatientSummaryType = {
  id: string;
  status: AppointmentStatuses;
  billingKey?: string;
  clinicId?: number;
  locationId?: number;
  appointmentDate?: Date | Dayjs | number | string;
  fname: string;
  lname: string;
  phone: string;
  email: string;
  displayName: string;
  clinician?: {
    id?: string;
    name?: string;
  };
  profilePhoto?: string;
  dateOfBirth?: string;
  accountNumber?: string;
};

export enum TransactionTypeEnum {
  Hybrid = 'Hybrid',
  Encounter = 'Encounter',
  AdHoc = 'AdHoc',
  Payment = 'Payment',
  Credit = 'Credit',
}

export type TransactionPatientBriefSummaryType = Partial<
  Omit<TransactionPatientSummaryType, 'status'>
>;

export type TransactionProviderType = {
  id: string;
  displayName: string;
};

export type TransactionSummaryType = {
  notes: string[];
  items?: string[];
};

export type TransactionActionType = {
  id: string;
  ts?: Date | number | string; // Saved in the table as a datetime. Always filled in if null.
  displayName?: string;
};

export enum TransactionCreditMethodEnum {
  Cash = 'Cash',
  Check = 'Check',
  Credit = 'Credit',
  ACH = 'ACH',
}

export enum TransactionItemSubtypeEnum {
  Adjustment = 'Adjustment',
  Payment = 'Payment',
  PatientPayment = 'Patient Payment',
  Service = 'Service',
  PatientRefund = 'Patient Refund',
  Supplement = 'Supplement or Supply',
  WriteOff = 'Write Off',
  PatientCredit = 'Patient Credit',
  Canceled = 'Canceled',
  NoShow = 'No-Show',
  Treatment = 'Treatment',
  Refund = 'Refund',
}

export enum TransactionItemTypeEnum {
  Credit = 'Credit',
  Debit = 'Debit',
}

export type TransactionItemNoteType = {
  notes: string[];
};

// sql/ddl/PatientTransactionItem.my.sql
export type PatientTransactionItemType = {
  id: number;
  patientTransactionId: number;
  code?: string | null;
  codeSet?: string | null;
  description?: string | null;
  type: TransactionItemTypeEnum | null;
  subtype: TransactionItemSubtypeEnum | null;
  invoiceId?: number | null;
  lineItemControlNumber?: string | null;
  amount: number | string | null;
  taxAmount?: number | string | null;
  units: number | null;
  modifier1?: string | null;
  modifier2?: string | null;
  modifier3?: string | null;
  modifier4?: string | null;
  locked?: boolean | false;
  notes?: string[] | null;
  method?: TransactionCreditMethodEnum | null;
  // payorReferenceId?: string | null;
  siblings?: number[] | null;
  diagnoses?: PatientTransactionItemDiagnosisType[] | null;
  payors?: Payor[] | null;
  // paymentId?: number | null;
  adjustment?: { percentOff?: number; structure: string };
  priceIncludesTax: boolean;
  salesTax?: boolean;
  productTaxRate?: number;
  serviceTaxRate?: number;
  paymentToward?: string;
  paymentSource?: string;
  createdAt?: Date | string;
  displayData?: any;
};

// sql/ddl/PatientTransactionItemDiagnosis.my.sql
export type PatientTransactionItemDiagnosisType = {
  id: number;
  itemId: number;
  code: string;
  codeSet: CodeSets;
  description: string;
  ord: number;
};

// Path: sql/ddl/PatientTransaction.my.sql
/**
 * When one of these is being viewed in the UI, the items
 * are done separately.
 */
export type PatientTransactionSummary = {
  id: number;
  number?: string | null;
  billingKey: string;
  clinicId: number;
  locationId: number;
  type: TransactionTypeEnum;
  subtype?: PaymentType | null;
  balance?: number;
  startingBalance?: number;
  created: TransactionActionType;
  updated: TransactionActionType;
  patient?: TransactionPatientType;
  provider?: TransactionProviderType;
  summary?: TransactionSummaryType;
  description?: string;
};

export type TransactionAppendItemsType = {
  services?: PatientTransactionItemType[];
  insurances?: AppointmentInsuranceType[] | Partial<AppointmentInsuranceType>[];
  courtesyBilling?: boolean;
};

export type PatientTransaction = PatientTransactionSummary & {
  // [key: string]: any;
  items: PatientTransactionItemType[];
  status?: AppointmentStatuses | null;
  taxDirty?: boolean | null;
  servicesDirty?: boolean | null;
  transactionDate?: Dayjs | number | string | undefined;
  patientSummary?: TransactionPatientBriefSummaryType | null;
  providerSummary?: any;
  disciplineId?: number | null;
  insurances?: AppointmentInsuranceType[] | Partial<AppointmentInsuranceType>[];
  payors?: Payor[];
  payments?: PatientPayment[];
  ePayRefNum?: string;
  ePayBatchKey?: string;
  referenceNumber?: string;
  serviceTaxRate?: number;
  productTaxRate?: number;
  amount?: number;
  services?: PatientTransactionItemType[];
  locked?: boolean;
  receiptDetail?: ReceiptDetails;
  patientBalance?: number | string;
  patientBilledAmount?: number | string;
  courtesyBilling?: boolean;
};

export const TransactionItemTypeOptions = Object.keys(
  TransactionItemTypeEnum,
).map((key) => ({ text: key, value: key }));

/**
 * This is the raw row from the database table.
 */
export type PatientTransactionRowType = {
  id: number;
  status: string;
  taxDirty: boolean;
  servicesDirty: boolean;
  billingKey: string;
  clinicId: number;
  locationId: number;
  type: TransactionTypeEnum;
  subtype: PaymentType;
  balance: number;
  startingBalance: number;
  createdId: string;
  updatedId: string;
  patientId: string;
  patientSummary: string;
  providerId: string;
  providerSummary: string;
  summary?: string;
  createdAt: Date;
  updatedAt: Date;
  transactionDate: Dayjs;
  productTaxRate: number;
  serviceTaxRate: number;
  ePayRefNum: string;
  ePayBatchKey: string;
  referenceNumber: string;
  description: string;
  disciplineId: number;
};

export enum TransactionActivityType {
  Created = 'transaction-created',
  Updated = 'transaction-updated',
  PayorInvoiced = 'payor-invoiced',
}

export type PatientTransactionActivity = {
  id?: string;
  billingKey: string;
  clinicId: number;
  createdAt?: number;
  updated: TransactionActionType;
  description: string;
  type: TransactionActivityType;
  data?: {
    updates?: ACTIVITY_CHANGE_TYPE[];
    strings?: string[];
  };
};

/**
 * This is a special-case of a PaymentTransactionItem. Simplifies
 * the payment view.
 */
export type PatientPaymentType = {
  id: number;
  number?: string | null;
  billingKey: string;
  amount: number;
  balance: number;
  paymentDate: string;
  isRefunded?: boolean;
  description: string;
  type: TransactionTypeEnum;
  receiptDetail: ReceiptDetails;
};

export type PatientTransactionSaveType = {
  ddb?: any;
  connection: Connection;
  updated: TransactionActionType;
  transaction: PatientTransaction;
  startDatabaseTransaction?: boolean;
  savingAsNew?: boolean;
  trace?: boolean;
  mockError?: boolean;
  syncPatientSummary?: boolean;
};

// export type PatientTransactionPayment = PatientTransactionItemType & {
//   paymentAmount: number;
//   transactionId: number;
//   summary?: { items: string[] };
// }
export type ReceiptDetails = {
  summary: {
    paymentTotal: number;
    total: {
      amount: number;
      label: string;
    };
    method: {
      type: string;
      label: string;
    };
    totalApplied: number;
    invoiceTotals: number;
    remainingBalance: number;
    paymentDate: string;
  };
  transactionPayments: TransactionPayment[];
  invoices: Partial<Invoice>[];
  isRefunded: boolean;
  number?: string;
};

export class PatientTransactionClass implements PatientTransaction {
  id: number;
  number?: string | null;
  transactionDate?: Dayjs | number | string | undefined;
  status?: AppointmentStatuses | null;
  taxDirty?: boolean | null;
  servicesDirty?: boolean | null;
  billingKey: string;
  clinicId: number;
  locationId: number;
  balance?: number;
  startingBalance?: number;
  type: TransactionTypeEnum;
  created: TransactionActionClass;
  updated: TransactionActionClass;
  patient?: TransactionPatientClass;
  patientSummary?: any;
  provider?: TransactionProviderType;
  providerSummary?: TransactionProviderType | undefined;
  description?: string;
  items: any[]; // Can't stand it, had to go with any.
  summary: any;
  patientBalance?: number | string;
  patientBilledAmount?: number | string;
  courtesyBilling?: boolean;
  onClick: (e: any, s: string) => void;

  constructor(
    transaction: PatientTransaction | PatientTransactionClass,
    onClick?: (billingKey: string) => void,
  ) {
    this.id = transaction.id;
    this.number = transaction.number;
    this.transactionDate = transaction.transactionDate;
    this.clinicId = transaction.clinicId;
    this.status = transaction.status;
    this.taxDirty = transaction.taxDirty;
    this.servicesDirty = transaction.servicesDirty;
    this.locationId = transaction.locationId;
    this.billingKey = transaction.billingKey;
    this.type = transaction.type;
    this.balance = transaction.balance;
    this.startingBalance = transaction.startingBalance;
    this.created = new TransactionActionClass(transaction.created);
    this.updated = new TransactionActionClass(transaction.updated);
    this.patient = transaction.patient
      ? new TransactionPatientClass(transaction.patient)
      : undefined;
    //TODO: Why are the following summary objects JSON?
    this.patientSummary = ChiroUpJSON.parse(transaction.patientSummary);
    this.providerSummary = ChiroUpJSON.parse(transaction.providerSummary);
    this.courtesyBilling = transaction.courtesyBilling;
    this.items = transaction.items.map((item) => item);
    if (onClick) {
      this.onClick = (_: any, s: string) => {
        onClick(s);
      };
    } else {
      this.onClick = () => {
        console.log('no onClick');
      };
    }
  }

  static initial = ({
    billingKey,
    patient,
    clinicId,
    locationId,
    type,
    created,
    provider,
    items,
    patientSummary,
    courtesyBilling = false,
  }: {
    billingKey: string;
    patient?: TransactionPatientType;
    clinicId: number;
    locationId: number;
    type: TransactionTypeEnum;
    created: TransactionActionType;
    provider?: TransactionProviderType;
    items?: PatientTransactionItemType[];
    patientSummary?: any;
    courtesyBilling?: boolean;
  }) => {
    return new PatientTransactionClass({
      id: -1,
      number: null,
      billingKey,
      clinicId,
      locationId,
      balance: 0,
      courtesyBilling,
      type,
      created,
      updated: created,
      patient: patient
        ? new TransactionPatientClass({
            id: patient.id,
            displayName: patient.displayName,
          })
        : undefined,
      patientSummary,
      items: items || [],
      provider: provider || undefined,
    });
  };

  static initialFromPatientSummary = (
    patientSummary: TransactionPatientSummaryType,
    user: TransactionActionType,
    patient?: Patient,
  ) => {
    const created = {
      id: user.id,
      displayName: user.displayName || 'Unknown.',
    };
    const provider = {
      id: patientSummary.clinician?.id,
      displayName: patientSummary.clinician?.name,
    };

    return {
      id: -1,
      billingKey: patientSummary.billingKey,
      clinicId: patientSummary.clinicId,
      locationId: patientSummary.locationId || -1,
      transactionDate: patientSummary.appointmentDate,
      balance: 0,
      startingBalance: 0,
      type: TransactionTypeEnum.Hybrid,
      subtype: null,
      created,
      updated: created,
      patient: {
        id: patientSummary.id,
        displayName: patientSummary.displayName,
      },
      patientSummary: {
        id: patientSummary.id,
        fname: patientSummary?.fname,
        lname: patientSummary?.lname,
        displayName: patientSummary.displayName,
        phone: patientSummary.phone || patient?.phone,
        email: patientSummary.email || patient?.email,
      },
      items: [],
      provider: provider,
      courtesyBilling: false,
    } as any; // ???bwm??? PatientTransaction
  };

  static initialFromAppointment = (
    appt: AppointmentClass,
    user: TransactionActionType,
    patient?: Patient,
    courtesyBilling = false,
  ) => {
    const created = {
      id: user.id,
      displayName: user.displayName || 'Unknown.',
    };
    const provider = {
      id: appt.clinicianId,
      displayName: appt.displayValues.clinicianName,
    };
    return {
      id: -1,
      billingKey: appt.id,
      clinicId: appt.clinicId,
      locationId: appt.locationId,
      balance: 0,
      type: TransactionTypeEnum.Hybrid,
      created,
      courtesyBilling,
      updated: created,
      patient: {
        id: appt.patientId,
        displayName: appt.displayValues.patientName,
      },
      patientSummary: {
        id: appt.patientId,
        fname: patient?.fname,
        lname: patient?.lname,
        displayName: appt.displayValues.patientName,
        phone: appt.displayValues.patientPhone || patient?.phone,
        email: appt.displayValues.patientEmail || patient?.email,
      },
      items: [],
      provider: provider,
    } as any; // ???bwm??? PatientTransaction
  };

  static initialFromPayment = async (
    connection: Connection,
    payment: PatientPayment,
    clinicId: number,
    user: TransactionActionType,
    credit: boolean,
    primaryTimezone: string,
  ) => {
    const billingKey = v4();
    let credits: any = [];

    if (payment.creditAmount) {
      // If this is a payment with a credit, we need to get all of the available credits for the account and create a transaction item for each of them that we need to use.
      const [creditsRes] = (await connection.query(
        `SELECT * FROM PatientTransaction WHERE patientId = :patientId AND type = :type AND balance > 0 ORDER BY transactionDate ASC;`,
        {
          patientId: payment.patientId,
          type: TransactionTypeEnum.Credit,
        },
      )) as unknown as PatientTransaction[][];
      credits = creditsRes;
    }

    const {
      items,
      itemBalance,
      creditTransactionsNeedUpdated,
    }: {
      items: PatientTransactionItemType[];
      itemBalance: number;
      creditTransactionsNeedUpdated: CreditTransactionsNeedUpdatedType[];
    } =
      payment.transactionPayments?.reduce(
        ({ items, itemBalance, creditTransactionsNeedUpdated }: any, tp) => {
          const tpPaymentAmount = Number(tp.paymentAmount || 0);
          items.push(
            toTransactionItemSubtype(TransactionItemSubtypeEnum.Payment, {
              ...tp,
              paymentToward: tp.billingKey,
            }),
          );
          if (credits?.length) {
            // If there are credits, we need to use them.
            for (const credit of credits) {
              const creditBalanceLeft = Number(credit.balance || '0');
              if (creditBalanceLeft >= tpPaymentAmount) {
                credit.balance -= tpPaymentAmount;
                creditTransactionsNeedUpdated.push({
                  billingKey: credit.billingKey,
                  item: toTransactionItemSubtype(
                    TransactionItemSubtypeEnum.Payment,
                    {
                      ...tp,
                      paymentToward: tp.billingKey,
                      paymentAmount: tpPaymentAmount,
                    },
                  ),
                });
                break;
              } else {
                creditTransactionsNeedUpdated.push({
                  billingKey: credit.billingKey,
                  item: toTransactionItemSubtype(
                    TransactionItemSubtypeEnum.Payment,
                    {
                      ...tp,
                      paymentToward: tp.billingKey,
                      paymentAmount: tpPaymentAmount,
                    },
                  ),
                });
                tp.paymentAmount -= creditBalanceLeft;
                credit.balance -= tp.paymentAmount;
                // creditTransactionsNeedUpdated.push(credit.billingKey); // ???BWM??? this array gets an object AND a string???
              }
            }
          }

          return {
            items,
            itemBalance: itemBalance + tp.paymentAmount,
            creditTransactionsNeedUpdated,
          };
        },
        {
          items: [],
          itemBalance: 0,
          creditTransactionsNeedUpdated:
            [] as CreditTransactionsNeedUpdatedType[],
        },
      ) || {};

    const created = {
      id: user.id,
      displayName: user.displayName || 'Unknown.',
    };
    let transactionDate = createUTCDayjs();
    const currentTime = dayjs().utc().tz(primaryTimezone);
    const createdAt = currentTime.format('YYYY-MM-DD HH:mm:ss');

    try {
      transactionDate = createUTCDayjs(payment.paymentDate);
    } catch (e) {
      console.error(`Could not parse date: ${payment.paymentDate}.`);
    }

    return {
      id: -1,
      number: null,
      billingKey,
      clinicId,
      description: payment.description,
      locationId: payment.locationId,
      patient: {
        id: payment.patientId,
        displayName: '',
      },
      balance: (payment.creditAmount || payment.amount) - itemBalance,
      startingBalance: payment.creditAmount || payment.amount,
      type: credit ? TransactionTypeEnum.Credit : TransactionTypeEnum.Payment,
      subtype: payment.type,
      amount: payment.amount,
      transactionDate: transactionDate?.valueOf(),
      createdAt,
      created,
      updated: created,
      items,
      ePayRefNum: payment.ePayRefNum,
      ePayBatchKey: payment.ePayBatchKey,
      creditTransactionsNeedUpdated,
      referenceNumber: String(payment?.referenceNumber),
    } as PatientTransaction & {
      creditTransactionsNeedUpdated: CreditTransactionsNeedUpdatedType[];
    };
  };

  get computedBalance() {
    return this.items.reduce((acc, item) => {
      switch (item.type) {
        case TransactionItemTypeEnum.Debit:
          acc = acc - item.amount;
          break;
        case TransactionItemTypeEnum.Credit:
          acc = acc + item.amount;
          break;
      }
      return acc;
    }, 0);
  }

  summaryObject() {
    return {
      id: this.id,
      number: this.number,
      billingKey: this.billingKey,
      clinicId: this.clinicId,
      locationId: this.locationId,
      balance: this.balance,
      type: this.type,
      created: this.created,
      updated: this.updated,
      patient: this.patient,
      items: this.items,
      provider: this.provider,
    } as PatientTransactionSummary;
  }

  get isOpen() {
    return !(this?.balance === 0 && this?.items.length !== 0);
  }

  get createdAt() {
    return ChiroUpDayJsCommon.display.datetime(this.created.ts);
  }

  get updatedAt() {
    return this.created.ts === this.updated.ts
      ? 'Never'
      : ChiroUpDayJsCommon.display.datetime(this.updated.ts);
  }
}

export const toTransactionItemSubtype = (
  subtype: TransactionItemSubtypeEnum | string,
  item: any,
) => {
  switch (subtype) {
    case TransactionItemSubtypeEnum.Treatment: {
      return {
        id: -1,
        patientTransactionId: -1,
        description: item.description,
        type: TransactionItemTypeEnum.Debit,
        subtype,
        units: 1,
        salesTax: item.salesTax,
        priceIncludesTax: item.priceIncludesTax,
        amount: item.amount,
      };
    }
    case TransactionItemSubtypeEnum.WriteOff: {
      return {
        id: -1,
        patientTransactionId: -1,
        description: 'Write-off',
        type: TransactionItemTypeEnum.Credit,
        subtype,
        units: 1,
        amount: item.amount,
      };
    }
    case TransactionItemSubtypeEnum.Supplement: {
      return {
        id: -1,
        patientTransactionId: -1,
        description: item.name,
        type: TransactionItemTypeEnum.Debit,
        subtype,
        amount: item.price,
        units: 1,
        groupId: item.groupId,
        salesTax: item.salesTax,
        priceIncludesTax: item.priceIncludesTax,
      };
    }
    case TransactionItemSubtypeEnum.Service: {
      return {
        id: -1,
        patientTransactionId: -1,
        code: item.code,
        codeSet: item.codeSet,
        description: item.description,
        type: TransactionItemTypeEnum.Debit,
        subtype,
        amount: item.billedAmount,
        units: 1,
        modifier1: item.modifier1,
        modifier2: item.modifier2,
        modifier3: item.modifier3,
        modifier4: item.modifier4,
        locked: false,
        groupId: item.groupId,
        salesTax: item.salesTax,
        priceIncludesTax: item.priceIncludesTax,
      };
    }
    case TransactionItemSubtypeEnum.Payment: {
      return {
        id: item.id ?? -1,
        patientTransactionId: item.patientTransactionId ?? -1,
        description: item.description,
        type: TransactionItemTypeEnum.Credit,
        subtype,
        amount:
          Number(item.paymentAmount) ||
          Number(item.amount) ||
          Number(item.creditAmount),
        units: 1,
        salesTax: 0, //TODO Change this when I know more about tax
        paymentToward: item.paymentToward,
        paymentSource: item.paymentSource,
        // priceIncludesTax: item.priceIncludesTax,
      };
    }
    case TransactionItemSubtypeEnum.Adjustment: {
      return {
        id: -1,
        patientTransactionId: -1,
        description: item.description,
        type: TransactionItemTypeEnum.Debit,
        subtype,
        amount: item.amount,
        units: 1,
        salesTax: 0,
      };
    }
    case TransactionItemSubtypeEnum.NoShow: {
      return {
        id: -1,
        patientTransactionId: -1,
        description: item.description,
        type: TransactionItemTypeEnum.Debit,
        subtype,
        amount: item.amount,
        units: 1,
        salesTax: 0,
      };
    }
    case TransactionItemSubtypeEnum.Canceled: {
      return {
        id: -1,
        patientTransactionId: -1,
        description: item.description,
        type: TransactionItemTypeEnum.Debit,
        subtype,
        amount: item.amount,
        units: 1,
        salesTax: 0,
      };
    }
    case TransactionItemSubtypeEnum.PatientRefund: {
      return {
        id: item.id ?? -1,
        patientTransactionId: item.patientTransactionId ?? -1,
        description: item.description,
        type: TransactionItemTypeEnum.Debit,
        subtype,
        amount: item.amount,
        units: 1,
        salesTax: 0,
      };
    }
  }

  return item;
};

export class TransactionPatientClass {
  id: string;
  displayName: string;

  constructor(patient: TransactionPatientType) {
    this.id = patient.id;
    this.displayName = patient.displayName;
  }
}

export class TransactionActionClass {
  id: string;
  ts?: number | string | Date;
  displayName?: string;

  constructor(action: TransactionActionType) {
    this.id = action.id;
    this.ts = action.ts;
    this.displayName = action.displayName;
  }
}

/**
 * Service line items can only be added via the encounter coding
 * tool.
 */
const vals = Object.values(TransactionItemSubtypeEnum).filter(
  (k) => k !== 'Service' && k !== 'Payment',
);
export const TransactionItemSubtypeOptions = Object.keys(
  TransactionItemSubtypeEnum,
)
  .filter((key) => key !== 'Service' && key !== 'Payment')
  .map((key, idx) => ({ text: vals[idx], value: key }));

/**
 * This is a SUBSET of the fields to build a transaction with the
 * extra details just for the patient. The duplicate fields in the
 * transaction are not included.
 */

export const TransactionCreditMethodOptions = Object.keys(
  TransactionCreditMethodEnum,
).map((key) => ({ text: key, value: key }));

const vals2 = Object.values(TransactionItemSubtypeEnum).filter(
  (k) =>
    k !== 'Service' && k !== 'Canceled' && k !== 'No-Show' && k !== 'Payment',
);
export const AdHocTransactionItemOptions = Object.keys(
  TransactionItemSubtypeEnum,
)
  .filter(
    (key) =>
      key !== 'Service' &&
      key !== 'Canceled' &&
      key !== 'NoShow' &&
      key !== 'Payment',
  )
  .map((key, idx) => ({ text: vals2[idx], value: key }));
